import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Stack,
  TextField,
  MenuItem,
  Menu,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { useSuccessAlert } from "../../../context/SuccessAlertContext/SuccessAlertContext";
import DataGrid from "../../../widgets/DataGrid/DataGrid";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";

import api_endpoints from "../../../api";
import { useApiClientQuery } from "../../../hooks/useApiClient";

const df = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "2-digit", day: "2-digit" });
const fmtDate = (d, fallback = "—") => {
  if (!d) return fallback;
  const v = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
  return Number.isNaN(v.getTime()) ? fallback : df.format(v);
};
const toISO = (d) => {
  if (!d) return "";
  const v = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
  if (Number.isNaN(v.getTime())) return "";
  return v.toISOString().slice(0, 10);
};

const EditPaymentMethodForm = ({ subscription, organizationId, onSuccess, onClose }) => {
  const { t } = useCustomTranslation("Recurring Payment");
  const [accountHolder, setAccountHolder] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountType, setAccountType] = useState("checking");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Tokenize the new payment method
      const tokenResponse = await api_endpoints.tokenizeBank({
        accountHolder,
        accountNumber,
        routingNumber,
        accountType,
        merchant: { organizationId, locationId: "loc_default" } // Assume default location
      });
      const newToken = tokenResponse.tokenId;

      // Update the subscription with the new paymethod token
      await api_endpoints.updateSubscriptionPaymethod(subscription.id, { paymethodToken: newToken });

      onSuccess();
    } catch (err) {
      console.error("Failed to update payment method", err);
      // Handle error, perhaps show alert
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label={t("account_holder")}
        value={accountHolder}
        onChange={(e) => setAccountHolder(e.target.value)}
        fullWidth
        required
      />
      <TextField
        label={t("account_number")}
        value={accountNumber}
        onChange={(e) => setAccountNumber(e.target.value)}
        fullWidth
        required
        type="number"
      />
      <TextField
        label={t("routing_number")}
        value={routingNumber}
        onChange={(e) => setRoutingNumber(e.target.value)}
        fullWidth
        required
        type="number"
      />
      <TextField
        select
        label={t("account_type")}
        value={accountType}
        onChange={(e) => setAccountType(e.target.value)}
        fullWidth
        required
      >
        <MenuItem value="checking">{t("checking")}</MenuItem>
        <MenuItem value="savings">{t("savings")}</MenuItem>
      </TextField>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={handleSubmit} disabled={loading} variant="contained">
          {loading ? t("updating") : t("update")}
        </Button>
      </DialogActions>
    </Stack>
  );
};

export const DetailsPanel = ({
  open,
  customerId,      // <- this must be the donor _id 
  rows,
  organizationId,
  refreshToken = 0,
  onClose,
  transactions = [],
  onPause,
  onResume,
  onCancel,
}) => {
  const { t } = useCustomTranslation("Recurring Payment");
  const chargeAttemptsLabel = t("charge_attempts");
  const loadingLabel = t("loading");
  const noValue = t("no_value");
  // Use a proper default for currency symbol since the translation may not exist
  const currencySymbol = t("currency_symbol", { defaultValue: "$" });

  const translateFrequencyValue = useCallback(
    (value) => {
      if (!value) return noValue;
      const key = String(value).toLowerCase();
      return t(key, { defaultValue: value });
    },
    [noValue, t]
  );

  const translatePaymentType = useCallback(
    (type) => {
      if (!type) return noValue;
      const raw = String(type);
      const key = raw.toLowerCase();
  const normalizedKey = key === "bank" || key === "eft" ? "ach" : key;
      return t(`payment_type_${normalizedKey}`, { defaultValue: raw.toUpperCase() });
    },
    [noValue, t]
  );

  const translateStatus = useCallback(
    (status) => {
      if (!status) return t("status_pending");
      const raw = String(status);
      const key = raw.toLowerCase();
      return t(`status_${key}`, {
        defaultValue: `${raw.charAt(0).toUpperCase()}${raw.slice(1)}`,
      });
    },
    [t]
  );

  const translateEmailStatus = useCallback(
    (status) => {
      if (!status) return "";
      const raw = String(status);
      const key = raw.toLowerCase();
      return t(`email_status_${key}`, { defaultValue: raw });
    },
    [t]
  );

  const formatMaskedValue = useCallback(
    (label, last4) => {
      const effectiveLabel = label ? String(label) : "";
      const digits = `${last4 ?? ""}`.trim();
      if (!digits) return effectiveLabel || noValue;
      return t("payment_method_mask", {
        label: effectiveLabel || t("payment_method"),
        last4: digits,
      });
    },
    [noValue, t]
  );

  const formatPaymentMethodDisplay = useCallback(
    (method, fallbackType) => {
      const payload = method || {};
      const typeValue = payload.type || payload.kind || fallbackType;
      if (typeValue) {
        const normalized = String(typeValue).toLowerCase();
        if (normalized === "card") {
          const brand = payload.brand || payload.brand_name || t("card_label");
          return formatMaskedValue(brand, payload.last4);
        }
        if (normalized === "bank" || normalized === "ach") {
          const bankName =
            payload.bankName || payload.bank_name || t("bank_label");
          return formatMaskedValue(bankName, payload.last4);
        }
        const label = translatePaymentType(normalized);
        return formatMaskedValue(label, payload.last4);
      }
      const label = payload.label || payload.name;
      return formatMaskedValue(label, payload.last4);
    },
    [formatMaskedValue, t, translatePaymentType]
  );

  // Keep hooks order stable
  const customerRowsRaw = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    if (!customerId) return [];
    return rows.filter((r) => r.customerId === customerId);
  }, [rows, customerId]);

  const firstRow = customerRowsRaw[0] ?? {};
  const customerName = firstRow?.customerName || "";

  // ---------- LAZY FETCH: Donor by ID ----------
  const donorQuery = useApiClientQuery(
    () => api_endpoints.getDonorById(customerId),
    {
      queryKey: ["drawer_donor_by_id", customerId],
      enabled: Boolean(open && customerId),
      staleTime: 30_000,
    }
  );

  const attemptsQuery = useApiClientQuery(
    () => api_endpoints.listRecurringChargeAttempts(organizationId, { donorId: customerId }),
    {
      queryKey: ["recurring_charge_attempts", organizationId, customerId, refreshToken],
      enabled: Boolean(open && customerId && organizationId),
      staleTime: 10_000,
    }
  );

  const transactionsQuery = useApiClientQuery(
    () => api_endpoints.listRecurringTransactions(organizationId, { donorId: customerId }),
    {
      queryKey: ["recurring_transactions", organizationId, customerId, refreshToken],
      enabled: Boolean(open && customerId && organizationId),
      staleTime: 10_000,
    }
  );

  const donorFromApi = donorQuery?.data || null;

  // ---------- Build read-only donor view with fallbacks ----------
  const donorView = useMemo(() => {
    // 1) Try local snapshot if present on the row
    const localSnapshot = firstRow.donorSnapshot || firstRow.donor || {};

    // 2) If we only have a display string with " · Company", strip company before splitting
    const nameOnly = (customerName || "").split(/\s+[·•]\s+/)[0]?.trim() || "";
    const parts = nameOnly.split(/\s+/).filter(Boolean);
    const fallbackFromName = {
      first_name: parts[0] || "",
      last_name: parts.length > 1 ? parts.slice(1).join(" ") : "",
    };

    // 3) Merge order: fallbacks -> local snapshot -> API (authoritative)
    const base = {
      email: "",
      email_status: "",
      first_name: fallbackFromName.first_name,
      last_name: fallbackFromName.last_name,
      phone: "",
      adresse: "",
      company: "",
    };

    const mergedLocal = {
      ...base,
      email: localSnapshot.email || base.email,
      email_status: localSnapshot.email_status || base.email_status,
      first_name: localSnapshot.first_name || base.first_name,
      last_name: localSnapshot.last_name || base.last_name,
      phone: localSnapshot.phone || base.phone,
      adresse: localSnapshot.adresse || localSnapshot.address || base.adresse,
      company: localSnapshot.company || base.company,
    };

    return donorFromApi ? { ...mergedLocal, ...donorFromApi } : mergedLocal;
  }, [donorFromApi, firstRow, customerName]);

  const resolvedDonorName = useMemo(() => {
    return `${donorView.first_name || ""} ${donorView.last_name || ""}`.trim();
  }, [donorView]);

  // ---------------- Tabs ----------------
  const [tab, setTab] = useState(0);

  // ---------------- Filters (subs tab) ----------------
  const uniqueFrequencies = useMemo(
    () => Array.from(new Set(customerRowsRaw.map((r) => r.frequency).filter(Boolean))),
    [customerRowsRaw]
  );
  const uniquePaymentTypes = useMemo(
    () => Array.from(new Set(customerRowsRaw.map((r) => r?.paymentMethod?.type).filter(Boolean))),
    [customerRowsRaw]
  );

  const [tableFilter, setTableFilter] = useState({
    reason: "",
    frequency: "",
    startDate: "",
    endDate: "",
    paymentType: "",
    amountMin: "",
    amountMax: "",
    status: "",
  });

  // ---------------- Actions menu state ----------------
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeRow, setActiveRow] = useState(null);

  const openMenuForRow = (evt, row) => {
    setMenuAnchor(evt.currentTarget);
    setActiveRow(row);
  };
  const closeMenu = () => setMenuAnchor(null);

  // ---------------- Dialogs state ----------------
  const [pauseResumeOpen, setPauseResumeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pauseMode, setPauseMode] = useState("pause"); // 'pause' | 'resume'
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // Refund state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [activeTransaction, setActiveTransaction] = useState(null);
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  // ---------------- Columns (subs) ----------------
  const columns = useMemo(
    () => [
      { field: "reason", headerName: t("reason"), sortable: true, numberSort: false },
      { field: "startDate", headerName: t("start_date"), sortable: true, numberSort: false },
      { field: "endDate", headerName: t("end_date"), sortable: true, numberSort: false },
      { field: "frequency", headerName: t("frequency"), sortable: true, numberSort: false },
      { field: "amount", headerName: t("amount"), sortable: true, numberSort: true },
      { field: "status", headerName: t("status"), sortable: false, numberSort: false },
      { field: "paymentMethodDisplay", headerName: t("payment_method"), sortable: false, numberSort: false },
      {
        field: "actions",
        headerName: t("actions"),
        sortable: false,
        numberSort: false,
        width: 64,
        renderCell: ({ row }) => (
          <IconButton size="small" onClick={(e) => openMenuForRow(e, row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [t]
  );

  const filters = useMemo(
    () => ({
      reason: (
        <TextField
          size="small"
          fullWidth
          placeholder={t("reason")}
          value={tableFilter.reason}
          onChange={(e) => setTableFilter((s) => ({ ...s, reason: e.target.value }))}
        />
      ),
      startDate: (
        <TextField
          type="date"
          size="small"
          label={t("start_date")}
          value={tableFilter.startDate}
          onChange={(e) => setTableFilter((s) => ({ ...s, startDate: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      ),
      endDate: (
        <TextField
          type="date"
          size="small"
          label={t("end_date")}
          value={tableFilter.endDate}
          onChange={(e) => setTableFilter((s) => ({ ...s, endDate: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      ),
      frequency: (
        <TextField
          select
          size="small"
          label={t("frequency")}
          value={tableFilter.frequency}
          onChange={(e) => setTableFilter((s) => ({ ...s, frequency: e.target.value }))}
          fullWidth
        >
          <MenuItem value="">{t("all")}</MenuItem>
          {uniqueFrequencies.map((f) => (
            <MenuItem key={f} value={f}>
              {translateFrequencyValue(f)}
            </MenuItem>
          ))}
        </TextField>
      ),
      amount: (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            type="number"
            placeholder={t("min")}
            value={tableFilter.amountMin}
            onChange={(e) => setTableFilter((s) => ({ ...s, amountMin: e.target.value }))}
            inputProps={{ min: 0 }}
            fullWidth
          />
          <TextField
            size="small"
            type="number"
            placeholder={t("max")}
            value={tableFilter.amountMax}
            onChange={(e) => setTableFilter((s) => ({ ...s, amountMax: e.target.value }))}
            inputProps={{ min: 0 }}
            fullWidth
          />
        </Box>
      ),
      status: (
        <TextField
          select
          size="small"
          label={t("status")}
          value={tableFilter.status}
          onChange={(e) => setTableFilter((s) => ({ ...s, status: e.target.value }))}
          fullWidth
        >
          <MenuItem value="">{t("all")}</MenuItem>
          <MenuItem value="running">{t("running")}</MenuItem>
          <MenuItem value="paused">{t("paused")}</MenuItem>
          <MenuItem value="deleted">{t("deleted")}</MenuItem>
        </TextField>
      ),
      paymentMethodDisplay: (
        <TextField
          select
          size="small"
          label={t("payment_type")}
          value={tableFilter.paymentType}
          onChange={(e) => setTableFilter((s) => ({ ...s, paymentType: e.target.value }))}
          fullWidth
        >
          <MenuItem value="">{t("all")}</MenuItem>
          {uniquePaymentTypes.map((tpe) => (
            <MenuItem key={tpe} value={tpe}>
              {translatePaymentType(tpe)}
            </MenuItem>
          ))}
        </TextField>
      ),
    }),
    [
      t,
      tableFilter.reason,
      tableFilter.startDate,
      tableFilter.endDate,
      tableFilter.frequency,
      tableFilter.amountMin,
      tableFilter.amountMax,
      tableFilter.status,
      tableFilter.paymentType,
      translateFrequencyValue,
      translatePaymentType,
      uniqueFrequencies,
      uniquePaymentTypes,
    ]
  );

  // ---------------- Map subs rows ----------------
  const subsRows = useMemo(() => {
    return customerRowsRaw
      .map((r) => ({
        ...r,
        id: r.id || r._id,
        startDate: fmtDate(r.startDate, noValue),
        endDate: fmtDate(r.endDate, noValue),
        startDateRaw: toISO(r.startDate),
        endDateRaw: toISO(r.endDate),
        amount: Number(r.amount),
        status: r.is_deleted ? "deleted" : (r.status === "active" ? "running" : r.status || "running"),
        paymentType: r?.paymentMethod?.type || "",
        paymentMethodDisplay: formatPaymentMethodDisplay(r.paymentMethod, r?.paymentMethod?.type),
      }))
      .filter((r) => {
        const matchReason =
          !tableFilter.reason ||
          (r.reason || "").toLowerCase().includes(tableFilter.reason.toLowerCase());
        const matchFreq = !tableFilter.frequency || r.frequency === tableFilter.frequency;
        const matchStart = !tableFilter.startDate || r.startDateRaw === tableFilter.startDate;
        const matchEnd = !tableFilter.endDate || r.endDateRaw === tableFilter.endDate;
        const matchType = !tableFilter.paymentType || r.paymentType === tableFilter.paymentType;
        const matchStatus = !tableFilter.status || r.status === tableFilter.status;

        const min = tableFilter.amountMin !== "" ? parseFloat(tableFilter.amountMin) : null;
        const max = tableFilter.amountMax !== "" ? parseFloat(tableFilter.amountMax) : null;
        const matchAmount =
          (min === null || (!Number.isNaN(min) && r.amount >= min)) &&
          (max === null || (!Number.isNaN(max) && r.amount <= max));

        return matchReason && matchFreq && matchStart && matchEnd && matchType && matchStatus && matchAmount;
      });
  }, [customerRowsRaw, formatPaymentMethodDisplay, noValue, tableFilter]);

  // ---------------- Transactions rows ----------------
  const txRows = useMemo(() => (Array.isArray(transactions) ? transactions : []), [transactions]);
  const resolvedTransactions = useMemo(() => {
    if (Array.isArray(transactionsQuery.data)) return transactionsQuery.data;
    return txRows;
  }, [transactionsQuery.data, txRows]);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  );

  const renderAmount = useCallback(
    (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return noValue;
      return `${currencySymbol}${currencyFormatter.format(numeric)}`;
    },
    [currencyFormatter, currencySymbol, noValue]
  );

  const formattedTransactions = useMemo(() => {
    return (resolvedTransactions || []).map((tx) => {
      const sub = tx.subscription_id || {};
      const snap = sub.payment_method_snapshot || {};
      const paymentMethodDisplay = formatPaymentMethodDisplay(snap, snap.kind);

      const amountValue = Number(tx.amount ?? sub.amount ?? 0);
      const createdAt = tx.created_at || tx.date || null;
      const dateObj = createdAt ? new Date(createdAt) : null;
      const reason = sub.reason || tx.reason || tx.campaign_id?.name || "";
      
      // Check if transaction is refunded (negative amount means it's a refund record)
      // or if there's a refunded_at field or refund status
      const isRefunded = tx.refunded || tx.refunded_at || tx.status === "refunded" || amountValue < 0;

      const dateIso = dateObj && !Number.isNaN(dateObj.getTime())
        ? dateObj.toISOString().slice(0, 10)
        : "";

      return {
        id: tx._id || tx.id,
        date: dateIso,
        dateDisplay: fmtDate(dateObj, noValue),
        amount: amountValue,
        amountDisplay: renderAmount(amountValue),
        paymentMethodDisplay,
        reason,
        isRefunded,
        status: isRefunded ? "refunded" : "completed",
      };
    });
  }, [formatPaymentMethodDisplay, noValue, renderAmount, resolvedTransactions]);

  const attemptsRows = useMemo(() => {
    const list = Array.isArray(attemptsQuery.data) ? attemptsQuery.data : [];
    return list.map((attempt) => {
      const sub = attempt.subscription_id || {};
      const snap = sub.payment_method_snapshot || {};
      const paymentMethodDisplay = formatPaymentMethodDisplay(snap, snap.kind);

      const amountValue = Number(attempt.amount ?? sub.amount ?? 0);
      const scheduledObj = attempt.scheduled_at ? new Date(attempt.scheduled_at) : null;
      const attemptedObj = attempt.attempted_at ? new Date(attempt.attempted_at) : null;

      const scheduledIso = scheduledObj && !Number.isNaN(scheduledObj.getTime())
        ? scheduledObj.toISOString().slice(0, 10)
        : "";
      const attemptedIso = attemptedObj && !Number.isNaN(attemptedObj.getTime())
        ? attemptedObj.toISOString().slice(0, 10)
        : "";

      return {
        id: attempt._id || attempt.id,
        scheduledAt: scheduledIso,
  scheduledDisplay: fmtDate(scheduledObj, noValue),
        attemptedAt: attemptedIso,
  attemptedDisplay: fmtDate(attemptedObj, noValue),
        amount: amountValue,
        amountDisplay: renderAmount(amountValue),
        paymentMethodDisplay,
        status: attempt.status || "pending",
        reason: sub.reason || "",
        failureMessage: attempt.failure_message || "",
        transactionId: attempt.transaction_id?._id || attempt.transaction_id || "",
      };
    });
  }, [attemptsQuery.data, formatPaymentMethodDisplay, noValue, renderAmount]);

  const transactionsLoading = transactionsQuery.isLoading || transactionsQuery.isFetching;
  const attemptsLoading = attemptsQuery.isLoading || attemptsQuery.isFetching;

  const [transactionsFilter, setTransactionsFilter] = useState({
    dateFrom: "",
    dateTo: "",
    reason: "",
    paymentMethod: "",
    amountMin: "",
    amountMax: "",
    status: "",
  });

  const [attemptsFilter, setAttemptsFilter] = useState({
    scheduledFrom: "",
    scheduledTo: "",
    status: "",
    reason: "",
    paymentMethod: "",
    amountMin: "",
    amountMax: "",
  });

  const filteredTransactions = useMemo(() => {
    return (formattedTransactions || []).filter((row) => {
      const dateIso = row.date || "";
      const matchDateFrom = !transactionsFilter.dateFrom || (dateIso && dateIso >= transactionsFilter.dateFrom);
      const matchDateTo = !transactionsFilter.dateTo || (dateIso && dateIso <= transactionsFilter.dateTo);

      const matchReason = !transactionsFilter.reason || (row.reason || "").toLowerCase().includes(transactionsFilter.reason.toLowerCase());
      const matchMethod = !transactionsFilter.paymentMethod || (row.paymentMethodDisplay || "").toLowerCase().includes(transactionsFilter.paymentMethod.toLowerCase());
      
      const matchStatus = !transactionsFilter.status || row.status === transactionsFilter.status;

      const amountValue = Number(row.amount ?? 0);
      const min = transactionsFilter.amountMin !== "" ? parseFloat(transactionsFilter.amountMin) : null;
      const max = transactionsFilter.amountMax !== "" ? parseFloat(transactionsFilter.amountMax) : null;
      const matchAmount =
        (min === null || (!Number.isNaN(min) && amountValue >= min)) &&
        (max === null || (!Number.isNaN(max) && amountValue <= max));

      return matchDateFrom && matchDateTo && matchReason && matchMethod && matchAmount && matchStatus;
    });
  }, [formattedTransactions, transactionsFilter]);

  const uniqueAttemptStatuses = useMemo(
    () => Array.from(new Set(attemptsRows.map((r) => r.status).filter(Boolean))),
    [attemptsRows]
  );

  const filteredAttempts = useMemo(() => {
    return attemptsRows.filter((row) => {
      const scheduledIso = row.scheduledAt || "";
      const matchScheduledFrom = !attemptsFilter.scheduledFrom || (scheduledIso && scheduledIso >= attemptsFilter.scheduledFrom);
      const matchScheduledTo = !attemptsFilter.scheduledTo || (scheduledIso && scheduledIso <= attemptsFilter.scheduledTo);

      const matchStatus = !attemptsFilter.status || row.status === attemptsFilter.status;
      const matchReason = !attemptsFilter.reason || (row.reason || "").toLowerCase().includes(attemptsFilter.reason.toLowerCase());
      const matchMethod = !attemptsFilter.paymentMethod || (row.paymentMethodDisplay || "").toLowerCase().includes(attemptsFilter.paymentMethod.toLowerCase());

      const amountValue = Number(row.amount ?? 0);
      const min = attemptsFilter.amountMin !== "" ? parseFloat(attemptsFilter.amountMin) : null;
      const max = attemptsFilter.amountMax !== "" ? parseFloat(attemptsFilter.amountMax) : null;
      const matchAmount =
        (min === null || (!Number.isNaN(min) && amountValue >= min)) &&
        (max === null || (!Number.isNaN(max) && amountValue <= max));

      return matchScheduledFrom && matchScheduledTo && matchStatus && matchReason && matchMethod && matchAmount;
    });
  }, [attemptsRows, attemptsFilter]);

  const transactionColumns = useMemo(
    () => [
      {
        field: "date",
        headerName: t("date"),
        sortable: true,
        numberSort: false,
        renderCell: ({ row }) => row.dateDisplay || noValue,
      },
      {
        field: "amount",
        headerName: t("amount"),
        sortable: true,
        numberSort: true,
        renderCell: ({ row }) => row.amountDisplay || noValue,
      },
      {
        field: "status",
        headerName: t("status"),
        sortable: false,
        numberSort: false,
        renderCell: ({ row }) => (
          <Chip
            label={row.isRefunded ? t("refunded", { defaultValue: "Refunded" }) : t("completed", { defaultValue: "Completed" })}
            color={row.isRefunded ? "warning" : "success"}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "paymentMethodDisplay",
        headerName: t("payment_method"),
        sortable: false,
        numberSort: false,
      },
      {
        field: "reason",
        headerName: t("reason"),
        sortable: false,
        numberSort: false,
      },
      {
        field: "actions",
        headerName: t("actions"),
        sortable: false,
        numberSort: false,
        width: 100,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            disabled={row.isRefunded}
            onClick={() => {
              setActiveTransaction(row);
              setRefundOpen(true);
            }}
          >
            {row.isRefunded ? t("refunded", { defaultValue: "Refunded" }) : t("refund")}
          </Button>
        ),
      },
    ],
    [noValue, t]
  );

  const transactionFilters = useMemo(
    () => ({
      date: (
        <Stack direction="row" spacing={1}>
          <TextField
            type="date"
            size="small"
            value={transactionsFilter.dateFrom}
            onChange={(e) =>
              setTransactionsFilter((s) => ({ ...s, dateFrom: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            label={t("from")}
            fullWidth
          />
          <TextField
            type="date"
            size="small"
            value={transactionsFilter.dateTo}
            onChange={(e) =>
              setTransactionsFilter((s) => ({ ...s, dateTo: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            label={t("to")}
            fullWidth
          />
        </Stack>
      ),
      amount: (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="number"
            placeholder={t("min")}
            value={transactionsFilter.amountMin}
            onChange={(e) =>
              setTransactionsFilter((s) => ({ ...s, amountMin: e.target.value }))
            }
            fullWidth
          />
          <TextField
            size="small"
            type="number"
            placeholder={t("max")}
            value={transactionsFilter.amountMax}
            onChange={(e) =>
              setTransactionsFilter((s) => ({ ...s, amountMax: e.target.value }))
            }
            fullWidth
          />
        </Stack>
      ),
      paymentMethodDisplay: (
        <TextField
          size="small"
          placeholder={t("payment_method")}
          value={transactionsFilter.paymentMethod}
          onChange={(e) =>
            setTransactionsFilter((s) => ({ ...s, paymentMethod: e.target.value }))
          }
          fullWidth
        />
      ),
      reason: (
        <TextField
          size="small"
          placeholder={t("reason")}
          value={transactionsFilter.reason}
          onChange={(e) =>
            setTransactionsFilter((s) => ({ ...s, reason: e.target.value }))
          }
          fullWidth
        />
      ),
      status: (
        <TextField
          select
          size="small"
          label={t("status")}
          value={transactionsFilter.status}
          onChange={(e) =>
            setTransactionsFilter((s) => ({ ...s, status: e.target.value }))
          }
          fullWidth
        >
          <MenuItem value="">{t("all")}</MenuItem>
          <MenuItem value="completed">{t("completed", { defaultValue: "Completed" })}</MenuItem>
          <MenuItem value="refunded">{t("refunded", { defaultValue: "Refunded" })}</MenuItem>
        </TextField>
      ),
    }),
    [
      t,
      transactionsFilter.dateFrom,
      transactionsFilter.dateTo,
      transactionsFilter.amountMin,
      transactionsFilter.amountMax,
      transactionsFilter.paymentMethod,
      transactionsFilter.reason,
      transactionsFilter.status,
    ]
  );

  const attemptColumns = useMemo(
    () => [
      {
        field: "scheduledAt",
        headerName: t("scheduled"),
        sortable: true,
        numberSort: false,
        renderCell: ({ row }) => row.scheduledDisplay || noValue,
      },
      {
        field: "attemptedAt",
        headerName: t("attempted"),
        sortable: true,
        numberSort: false,
        renderCell: ({ row }) => row.attemptedDisplay || noValue,
      },
      {
        field: "amount",
        headerName: t("amount"),
        sortable: true,
        numberSort: true,
        renderCell: ({ row }) => row.amountDisplay || noValue,
      },
      {
        field: "paymentMethodDisplay",
        headerName: t("payment_method"),
        sortable: false,
        numberSort: false,
      },
      {
        field: "status",
        headerName: t("status"),
        sortable: false,
        numberSort: false,
        renderCell: ({ row }) => {
          const chipColor =
            row.status === "succeeded"
              ? "success"
              : row.status === "failed"
                ? "error"
                : row.status === "skipped"
                  ? "warning"
                  : "default";
          return (
            <Chip
              size="small"
              color={chipColor}
              label={
                translateStatus(row.status)
              }
            />
          );
        },
      },
      {
        field: "details",
        headerName: t("details"),
        sortable: false,
        numberSort: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.5} sx={{ py: 0.5 }}>
            <Typography variant="body2" color="text.primary">
              {row.reason || noValue}
            </Typography>
            {row.failureMessage ? (
              <Typography variant="caption" color="error">
                {row.failureMessage}
              </Typography>
            ) : null}
            {row.transactionId ? (
              <Typography variant="caption" color="text.secondary">
                {t("transaction_reference", { id: row.transactionId })}
              </Typography>
            ) : null}
          </Stack>
        ),
      },
    ],
    [noValue, t, translateStatus]
  );

  const attemptFilters = useMemo(
    () => ({
      scheduledAt: (
        <Stack direction="row" spacing={1}>
          <TextField
            type="date"
            size="small"
            value={attemptsFilter.scheduledFrom}
            onChange={(e) =>
              setAttemptsFilter((s) => ({ ...s, scheduledFrom: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            label={t("from")}
            fullWidth
          />
          <TextField
            type="date"
            size="small"
            value={attemptsFilter.scheduledTo}
            onChange={(e) =>
              setAttemptsFilter((s) => ({ ...s, scheduledTo: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            label={t("to")}
            fullWidth
          />
        </Stack>
      ),
      amount: (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="number"
            placeholder={t("min")}
            value={attemptsFilter.amountMin}
            onChange={(e) =>
              setAttemptsFilter((s) => ({ ...s, amountMin: e.target.value }))
            }
            fullWidth
          />
          <TextField
            size="small"
            type="number"
            placeholder={t("max")}
            value={attemptsFilter.amountMax}
            onChange={(e) =>
              setAttemptsFilter((s) => ({ ...s, amountMax: e.target.value }))
            }
            fullWidth
          />
        </Stack>
      ),
      paymentMethodDisplay: (
        <TextField
          size="small"
          placeholder={t("payment_method")}
          value={attemptsFilter.paymentMethod}
          onChange={(e) =>
            setAttemptsFilter((s) => ({ ...s, paymentMethod: e.target.value }))
          }
          fullWidth
        />
      ),
      status: (
        <TextField
          select
          size="small"
          label={t("status")}
          value={attemptsFilter.status}
          onChange={(e) =>
            setAttemptsFilter((s) => ({ ...s, status: e.target.value }))
          }
          fullWidth
        >
          <MenuItem value="">{t("all")}</MenuItem>
          {uniqueAttemptStatuses.map((statusKey) => (
            <MenuItem key={statusKey} value={statusKey}>
              {translateStatus(statusKey)}
            </MenuItem>
          ))}
        </TextField>
      ),
      details: (
        <TextField
          size="small"
          placeholder={t("reason")}
          value={attemptsFilter.reason}
          onChange={(e) =>
            setAttemptsFilter((s) => ({ ...s, reason: e.target.value }))
          }
          fullWidth
        />
      ),
    }),
    [
      t,
      attemptsFilter.scheduledFrom,
      attemptsFilter.scheduledTo,
      attemptsFilter.amountMin,
      attemptsFilter.amountMax,
      attemptsFilter.paymentMethod,
      attemptsFilter.status,
      attemptsFilter.reason,
      translateStatus,
      uniqueAttemptStatuses,
    ]
  );

  // ---------------- Menu actions ----------------
  const doPause = () => {
    closeMenu();
    setPauseMode("pause");
    setPauseResumeOpen(true);
  };
  const doResume = () => {
    closeMenu();
    setPauseMode("resume");
    setPauseResumeOpen(true);
  };
  const doCancel = () => {
    closeMenu();
    setCancelOpen(true);
  };

  // ---------------- Dialog submit handlers ----------------
  const handleConfirmPauseResume = () => {
    if (!activeRow) return setPauseResumeOpen(false);
    if (pauseMode === "pause") onPause?.(activeRow);
    else onResume?.(activeRow);
    setPauseResumeOpen(false);
  };

  const handleConfirmCancel = () => {
    if (!activeRow) return setCancelOpen(false);
    onCancel?.(activeRow);
    setCancelOpen(false);
  };

  const handleRefundSubmit = async () => {
    if (!activeTransaction) return;
    setRefundLoading(true);
    try {
      // Full refund only - no amount or reason needed
      await api_endpoints.refundRecurringTransaction(activeTransaction.id, {});
      setMessageSuccessAlert(t("refund_success", { defaultValue: "Refund processed successfully" }));
      setShowSuccessAlert(true);
      transactionsQuery.refetch();
      setRefundOpen(false);
    } catch (e) {
      console.error(e);
    } finally {
      setRefundLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", md: 1280, lg: 1440 }, maxWidth: "100vw" } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, wordBreak: "break-word" }}>
              {customerName || noValue}
            </Typography>

            {/* Donor information (read-only) */}
            <div className="[&>div]:space-x-2 [&>div]:text-xl space-y-1">
              {/* Email */}
              <div className="!text-2xl font-semibold flex items-center gap-2">
                <EmailOutlinedIcon />
                <label>
                  {donorQuery.isLoading ? loadingLabel : donorView.email || noValue}
                </label>
                {!!donorView.email_status && donorView.email_status !== "valid" && (
                  <Chip
                    size="small"
                    icon={<WarningAmberOutlinedIcon />}
                    label={translateEmailStatus(donorView.email_status)}
                    color={donorView.email_status === "bounced" ? "error" : "warning"}
                    sx={{ ml: 1 }}
                  />
                )}
              </div>

              {/* Name */}
              <div className="text-xl flex items-center gap-2">
                <PersonOutlinedIcon />
                <label>
                  {donorQuery.isLoading ? loadingLabel : resolvedDonorName || noValue}
                </label>
              </div>

              {/* Phone */}
              <div className="text-xl flex items-center gap-2">
                <PhoneOutlinedIcon />
                <label>{donorQuery.isLoading ? loadingLabel : donorView.phone || noValue}</label>
              </div>

              {/* Address */}
              <div className="text-xl flex items-center gap-2">
                <HomeOutlinedIcon />
                <label>{donorQuery.isLoading ? loadingLabel : donorView.adresse || noValue}</label>
              </div>

              {/* Company */}
              <div className="text-xl flex items-center gap-2">
                <WorkOutlineOutlinedIcon />
                <label>{donorQuery.isLoading ? loadingLabel : donorView.company || noValue}</label>
              </div>
            </div>
          </Box>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
        <Tab label={t("recurring_payments")} />
        <Tab label={t("transactions")} />
        <Tab label={chargeAttemptsLabel} />
      </Tabs>

      {/* Body */}
      <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
        {tab === 0 ? (
          <DataGrid columns={columns} rows={subsRows} filters={/* same filters as above */{
            reason: (
              <TextField
                size="small"
                fullWidth
                placeholder={t("reason")}
                value={tableFilter.reason}
                onChange={(e) => setTableFilter((s) => ({ ...s, reason: e.target.value }))}
              />
            ),
            startDate: (
              <TextField
                type="date"
                size="small"
                label={t("start_date")}
                value={tableFilter.startDate}
                onChange={(e) => setTableFilter((s) => ({ ...s, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            ),
            endDate: (
              <TextField
                type="date"
                size="small"
                label={t("end_date")}
                value={tableFilter.endDate}
                onChange={(e) => setTableFilter((s) => ({ ...s, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            ),
            frequency: (
              <TextField
                select
                size="small"
                label={t("frequency")}
                value={tableFilter.frequency}
                onChange={(e) => setTableFilter((s) => ({ ...s, frequency: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">{t("all")}</MenuItem>
                {uniqueFrequencies.map((f) => (
                  <MenuItem key={f} value={f}>
                    {translateFrequencyValue(f)}
                  </MenuItem>
                ))}
              </TextField>
            ),
            amount: (
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder={t("min")}
                  value={tableFilter.amountMin}
                  onChange={(e) => setTableFilter((s) => ({ ...s, amountMin: e.target.value }))}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder={t("max")}
                  value={tableFilter.amountMax}
                  onChange={(e) => setTableFilter((s) => ({ ...s, amountMax: e.target.value }))}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Box>
            ),
            status: (
              <TextField
                select
                size="small"
                label={t("status")}
                value={tableFilter.status}
                onChange={(e) => setTableFilter((s) => ({ ...s, status: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">{t("all")}</MenuItem>
                <MenuItem value="running">{t("running")}</MenuItem>
                <MenuItem value="paused">{t("paused")}</MenuItem>
                <MenuItem value="deleted">{t("deleted")}</MenuItem>
              </TextField>
            ),
            paymentMethodDisplay: (
              <TextField
                select
                size="small"
                label={t("payment_type")}
                value={tableFilter.paymentType}
                onChange={(e) => setTableFilter((s) => ({ ...s, paymentType: e.target.value })) }
                fullWidth
              >
                <MenuItem value="">{t("all")}</MenuItem>
                {uniquePaymentTypes.map((tpe) => (
                  <MenuItem key={tpe} value={tpe}>
                    {translatePaymentType(tpe)}
                  </MenuItem>
                ))}
              </TextField>
            ),
          }} loading={false} />
        ) : tab === 1 ? (
          <DataGrid
            columns={transactionColumns}
            rows={filteredTransactions}
            filters={transactionFilters}
            loading={transactionsLoading}
          />
        ) : (
          <DataGrid
            columns={attemptColumns}
            rows={filteredAttempts}
            filters={attemptFilters}
            loading={attemptsLoading}
          />
        )}
      </Box>

      {/* Row actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {activeRow?.status === "paused" ? (
          <MenuItem onClick={doResume}>{t("resume")}</MenuItem>
        ) : (
          <MenuItem onClick={doPause}>{t("pause")}</MenuItem>
        )}
        <MenuItem onClick={doCancel} sx={{ color: "error.main" }}>
          {t("cancel")}
        </MenuItem>
        {activeRow?.paymentMethod?.type === "ach" && (
          <MenuItem onClick={() => { closeMenu(); setEditingRow(activeRow); setEditPaymentOpen(true); }}>
            {t("edit_payment_method")}
          </MenuItem>
        )}
      </Menu>

      {/* Pause/Resume dialog */}
      <Dialog open={pauseResumeOpen} onClose={() => setPauseResumeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pauseMode === "pause" ? t("pause_subscription") : t("resume_subscription")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {pauseMode === "pause" ? t("are_you_sure_pause") : t("are_you_sure_resume")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseResumeOpen(false)}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleConfirmPauseResume}>
            {pauseMode === "pause" ? t("pause") : t("resume")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("confirm_cancel")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {t("are_you_sure_cancel")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>{t("back")}</Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel}>
            {t("cancel_subscription")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Method dialog */}
      <Dialog open={editPaymentOpen} onClose={() => { setEditPaymentOpen(false); setEditingRow(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{t("edit_payment_method")}</DialogTitle>
        <DialogContent dividers>
          <EditPaymentMethodForm
            subscription={editingRow}
            organizationId={organizationId}
            onSuccess={() => { setEditPaymentOpen(false); setEditingRow(null); refreshToken(); }}
            onClose={() => { setEditPaymentOpen(false); setEditingRow(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundOpen} onClose={() => setRefundOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("refund_transaction")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              {t("refund_amount")}: {activeTransaction?.amountDisplay}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("full_refund_only_notice", { defaultValue: "This will issue a full refund for the transaction." })}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundOpen(false)}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleRefundSubmit} disabled={refundLoading}>
            {refundLoading ? t("processing") : t("refund")}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};
